import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalendarDialog } from "@/components/CalendarDialog";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/fullscale-logo.png";
import { cn } from "@/lib/utils";

const navLinks = [
  { label: "Home", href: "#hero-section" },
  { label: "System", href: "#system-section" },
  { label: "Results", href: "#results-section" },
  { label: "Process", href: "#process-section" },
  { label: "FAQs", href: "#faq-section" },
  { label: "Contact", href: "#contact-section" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("#hero-section");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = navLinks.map((link) => link.href.substring(1));
      for (const sectionId of sections.reverse()) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(`#${sectionId}`);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed top-0 left-0 right-0 z-[9999] transition-all duration-500",
          isScrolled
            ? "glass-header shadow-header border-b border-border/30"
            : "bg-transparent"
        )}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[64px] lg:h-[80px]">
            {/* Logo */}
            <a
              href="#hero-section"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#hero-section");
              }}
              className="flex-shrink-0 transition-all duration-300 hover:opacity-80"
            >
              <img
                src={logo}
                alt="FullScale Marketing - Digital Marketing Agency for Australian Tradies"
                className="h-9 sm:h-10 lg:h-11 w-auto object-contain"
                width={176}
                height={44}
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.href);
                  }}
                  className={cn(
                    "nav-link py-2 text-[15px] font-medium transition-all duration-300 whitespace-nowrap",
                    activeSection === link.href
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground"
                  )}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center">
              <CalendarDialog buttonVariant="default" buttonSize="header" />
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2.5 text-foreground hover:bg-muted rounded-xl transition-all duration-300"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </motion.div>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu - OUTSIDE header for proper z-index stacking */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden fixed inset-0 top-[64px] z-[9998] bg-card backdrop-blur-xl overflow-hidden"
          >
            <div className="h-full flex flex-col px-6 py-8 overflow-y-auto">
              <nav className="flex flex-col gap-2 flex-1" aria-label="Mobile navigation">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className={cn(
                      "text-left text-lg font-medium px-5 py-4 rounded-xl transition-all duration-300",
                      activeSection === link.href
                        ? "text-primary bg-primary/10"
                        : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                    )}
                  >
                    {link.label}
                  </motion.a>
                ))}
              </nav>

              {/* Mobile Menu CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="pt-6 border-t border-border/40"
              >
                <CalendarDialog
                  buttonVariant="hero"
                  buttonSize="xl"
                  buttonClassName="w-full justify-center h-14 text-lg rounded-xl"
                />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}